import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import './Cards.css';
import { getDeviceInfo as apiDeviceInfo } from '../services/device.js';

const EMPTY_PLACEHOLDER = '-';

const SLOPE_FIELDS = [
    { label: '邊坡名稱', field: 'slope_name' },
    { label: '邊坡類型', field: 'slope_type' },
    { label: '地層', field: 'layer' },
    { label: '保全對象', field: 'asset_type' },
    { label: '階數/坡高/坡度', combine: ['step', 'slope_height', 'angle'] },
    { label: '環境地質', field: 'geology' },
    { label: '地質災害潛勢', field: 'geo_tendency' },
    { label: '鄰近斷層', field: 'near_by_fault' },
    { label: '邊坡分級', field: 'slope_level' },
];

const SEGMENT_FIELD = 'district';

const formatDisplayValue = (value) => {
    if (value == null) return EMPTY_PLACEHOLDER;
    if (Array.isArray(value)) {
        const filtered = value.filter((item) => item != null && `${item}`.trim() !== '');
        return filtered.length ? filtered.join(' / ') : EMPTY_PLACEHOLDER;
    }
    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : EMPTY_PLACEHOLDER;
    }
    if (typeof value === 'string') {
        const trimmed = value.trim();
        return trimmed || EMPTY_PLACEHOLDER;
    }
    return `${value}`;
};

export default function SlopeInfo({ styleColor, itemColor1, itemColor2, deviceId }) {
    const [deviceInfo, setDeviceInfo] = useState(null);

    useEffect(() => {
        if (!deviceId) {
            setDeviceInfo(null);
            return;
        }

        let canceled = false;
        (async () => {
            try {
                const data = await apiDeviceInfo(deviceId);
                if (!canceled) setDeviceInfo(data || null);
            } catch (error) {
                if (!canceled) {
                    // eslint-disable-next-line no-console
                    console.error('Error fetching slope info:', error);
                }
            }
        })();

        return () => {
            canceled = true;
        };
    }, [deviceId]);

    const slopeDetails = useMemo(
        () =>
            SLOPE_FIELDS.map((item) => {
                let rawValue;
                if (Array.isArray(item.combine)) {
                    const combined = item.combine
                        .map((key) => deviceInfo?.[key])
                        .filter((val) => val != null && `${val}`.trim() !== '');
                    rawValue = combined.length ? combined.join(' / ') : undefined;
                } else {
                    rawValue = deviceInfo?.[item.field];
                }
                return {
                    label: item.label,
                    value: formatDisplayValue(rawValue),
                };
            }),
        [deviceInfo]
    );

    const slopeSegment = useMemo(
        () => formatDisplayValue(deviceInfo?.[SEGMENT_FIELD]),
        [deviceInfo]
    );

    const title =
        slopeSegment && slopeSegment !== EMPTY_PLACEHOLDER
            ? `邊坡資訊(${slopeSegment})`
            : '邊坡資訊';

    return (
        <div className="card geological">
            <h3 className="card-title" style={{ color: styleColor }}>
                {title}
            </h3>
            <div className="card-content">
                {slopeDetails.map((d, idx) => (
                    <div
                        className="item"
                        key={d.label}
                        style={{
                            backgroundColor: idx % 2 === 0 ? itemColor1 : itemColor2,
                        }}
                    >
                        <span className="label">{d.label}</span>
                        <span className="value">{d.value}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

SlopeInfo.propTypes = {
    styleColor: PropTypes.string,
    itemColor1: PropTypes.string,
    itemColor2: PropTypes.string,
    deviceId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

SlopeInfo.defaultProps = {
    styleColor: '#000',
    itemColor1: '#E0E0E0',
    itemColor2: '#D0D0D0',
    deviceId: undefined,
};
